// Centralized site content for Nikhan Controls
import {
  Cpu, Zap, Battery, Wrench, Factory, ShieldCheck, CircuitBoard,
  CarFront, Coffee, Plug, Flame, Settings2, Layers, Boxes,
  Lightbulb, Target, Award, Users, Clock, Globe
} from "lucide-react";

export const COMPANY = {
  name: "Nikhan Controls",
  legal: "Nikhan Control (OPC) Pvt. Ltd.",
  tagline: "Built For Your Success",
  established: 2019,
  location: "Mohali, Punjab, India",
  short: "From embedded systems to EV chargers and high-speed transformers — we engineer, manufacture and ship reliable electronics for industries that build the future.",
  vision: "To be a trusted partner in electronics innovation.",
  mission: "To deliver reliable, high-quality solutions with lasting value.",
  email: "info@nikhancontrols.com",
  phone: "+91 98000 00000",
  address: "Industrial Area, Phase 8B, Mohali, Punjab 160071, India",
  logoColor: "/assets/nc-logo.png",
  logoWhite: "/assets/nc-logo-white.png",
};

export const NAV = [
  { label: "Home", to: "/v1" },
  { label: "About", to: "/about" },
  { label: "Capabilities", to: "/services" },
  { label: "Products", to: "/products" },
  { label: "Industries", to: "/industries" },
  { label: "Contact", to: "/contact" },
];

export const STOCK = {
  pcbMacro1:   "/assets/powerSystem.avif",
  pcbMacro2:   "/assets/pcb.avif",
  pcbAssembly: "/assets/pcb.avif",
  evCharging:  "/assets/evCharger.avif",
  evCharger2:  "/assets/evCharger.avif",
  engineer:    "/assets/engineer.avif",
  engineerWork:"/assets/engineerWork.avif",
  factory:     "/assets/factory.avif",
  robotics:    "/assets/robotics.avif",
  serverRoom:  "/assets/serverRoom.avif",
  smartPlug:   "/assets/smartPlug.avif",
  controlPanel:"/assets/controlPanel.avif",
  windTurbines:"/assets/windTurbines.avif",
  solarPanel:  "/assets/solarPanel.avif",
  fireSafety:  "/assets/fireSafety.avif",
  vending:     "/assets/vending.avif",
  transformer: "/assets/transformer.avif",
};

export const HERO_SLIDES = [
  {
    eyebrow: "Electronics Manufacturing · Mohali, India",
    titleTop: "Built For",
    titleAccent: "Your Success.",
    subtitle: "Concept to certified production — embedded systems, power electronics and EMS engineered to outlast.",
    image: STOCK.pcbMacro1,
    chip: "EMS · Embedded · Power",
  },
  {
    eyebrow: "EV & Mobility",
    titleTop: "Powering the",
    titleAccent: "EV revolution.",
    subtitle: "Smart AC and DC chargers for two- and four-wheelers — safety-compliant, OCPP-ready, manufactured at scale.",
    image: STOCK.evCharging,
    chip: "EV Chargers · BMS · OCPP",
  },
  {
    eyebrow: "Precision Engineering",
    titleTop: "Custom hardware,",
    titleAccent: "proven at scale.",
    subtitle: "Multi-layer PCBs, control panels, SMPS systems and high-speed transformers — built to industrial spec.",
    image: STOCK.pcbMacro2,
    chip: "PCBA · SMPS · Transformers",
  },
  {
    eyebrow: "End-to-End Partner",
    titleTop: "From idea to",
    titleAccent: "shipped product.",
    subtitle: "Design, prototype, manufacture, test and deliver — one accountable engineering partner.",
    image: STOCK.factory,
    chip: "Design · Build · Ship",
  },
];

export const FACTORY_VIDEO = {
  // Real factory tour — YouTube
  youtubeId: "d1in7JGqNiI",
  embedUrl: "https://www.youtube.com/embed/d1in7JGqNiI?autoplay=1&rel=0&modestbranding=1&playsinline=1",
  // Poster is a real frame from the video (YouTube auto-generated thumbnail)
  poster: "https://i.ytimg.com/vi/d1in7JGqNiI/maxresdefault.jpg",
  posterFallback: "https://i.ytimg.com/vi/d1in7JGqNiI/hqdefault.jpg",
  title: "Inside our Mohali facility",
};

export const STATS = [
  { label: "Years of Expertise",   value: "7+",   icon: Clock },
  { label: "Successful Projects",  value: "120+", icon: Award },
  { label: "Skilled Workforce",    value: "40+",  icon: Users }, 
  { label: "Industries Served",    value: "8+",   icon: Globe },
];

export const SERVICES = [
  {
    slug: "embedded-system-design",
    title: "Embedded System Design",
    short: "Smart, reliable systems for vending machines, fire safety, car-wash controllers and more.",
    description: "We engineer end-to-end embedded systems with custom firmware, microcontroller-based hardware and IoT connectivity — from architecture to certified production.",
    icon: Cpu,
    bullets: ["Microcontroller-based hardware", "Firmware & RTOS", "IoT & wireless connectivity", "Functional safety logic"],
  },
  {
    slug: "high-speed-transformer-design",
    title: "High-Speed Transformer Design",
    short: "Compact, efficient transformers for SMPS, EV and industrial use.",
    description: "Precision-wound, high-frequency transformers optimized for size, efficiency and thermal performance — built for next-generation power conversion.",
    icon: Zap,
    bullets: ["High-frequency SMPS transformers", "EV charger magnetics", "Custom flybacks & inductors", "Thermal optimisation"],
  },
  {
    slug: "power-system-design",
    title: "Power System Design",
    short: "Efficient SMPS, custom EV chargers, battery management and EMI/EMC compliance.",
    description: "Full-stack power electronics — from architecture and topology selection to BMS, protection circuits and compliance testing.",
    icon: Battery,
    bullets: ["Custom SMPS (AC-DC, DC-DC)", "EV charging (2W / 4W)", "Battery management systems", "EMI / EMC compliance"],
  },
  {
    slug: "mechanical-assembly",
    title: "Mechanical Assembly",
    short: "Durable enclosures, CNC machining, wiring harness and final packaging.",
    description: "Robust mechanical engineering and assembly for harsh industrial environments — enclosures, harnesses and finished system integration.",
    icon: Wrench,
    bullets: ["Custom enclosures & sheet metal", "Wire harness assembly", "CNC machining & 3D printing", "Final QA & packaging"],
  },
  {
    slug: "electronic-manufacturing-services",
    title: "Electronic Manufacturing Services",
    short: "From prototype to full production — PCB assembly, component sourcing and integration.",
    description: "Turnkey EMS — DFM, supply-chain, SMT / through-hole assembly, testing and box-build under one roof.",
    icon: Factory,
    bullets: ["SMT & through-hole assembly", "Multi-layer PCB fabrication", "Component sourcing", "Box build & testing"],
  },
  {
    slug: "quality-and-testing",
    title: "Quality & Testing",
    short: "Functional, in-circuit and environmental testing for long-term reliability.",
    description: "Rigorous QA processes including AOI, ICT, functional testing and burn-in to guarantee field-proven reliability.",
    icon: ShieldCheck,
    bullets: ["AOI & ICT", "Functional & burn-in testing", "EMI / EMC validation", "Lifecycle reliability"],
  },
];

export const PRODUCTS = [
  { slug: "ev-chargers",                title: "EV Chargers (2W & 4W)",        short: "Smart, safety-compliant AC and DC chargers for electric two- and four-wheelers.",         icon: CarFront,     image: STOCK.evCharging,   labels: ["AC", "DC fast", "OCPP-ready"] },
  { slug: "smps-power-systems",         title: "SMPS Power Systems",           short: "High-efficiency switch-mode power supplies for industrial and consumer applications.",   icon: Zap,          image: STOCK.pcbMacro1,    labels: ["AC-DC", "DC-DC", "Custom"] },
  { slug: "smart-plugs",                title: "Smart Plugs",                  short: "Wi-Fi & BLE connected plugs with energy monitoring and remote scheduling.",              icon: Plug,         image: STOCK.smartPlug,    labels: ["IoT", "Wi-Fi", "Energy"] },
  { slug: "coffee-vending-controllers", title: "Coffee Vending Controllers",   short: "Programmable controllers for beverage and vending machines with telemetry.",             icon: Coffee,       image: STOCK.vending,      labels: ["Embedded", "HMI", "Telemetry"] },
  { slug: "fire-safety-systems",        title: "Fire Safety Systems",          short: "Detection, alarm and suppression electronics built for safety-critical environments.",   icon: Flame,        image: STOCK.fireSafety,   labels: ["Safety", "Detection", "Compliance"] },
  { slug: "custom-control-panels",      title: "Custom Control Panels",        short: "Industrial control panels engineered to spec — wired, tested and certified.",            icon: Settings2,    image: STOCK.controlPanel, labels: ["Industrial", "Custom", "Certified"] },
  { slug: "multi-layer-pcbs",           title: "Multi-Layer PCBs",             short: "High-density, multi-layer PCBs with HDI, blind/buried vias and impedance control.",       icon: Layers,       image: STOCK.pcbMacro2,    labels: ["HDI", "Multilayer", "Impedance"] },
  { slug: "transformers",               title: "High-Speed Transformers",      short: "Compact, high-frequency transformers for power conversion and isolation.",                icon: CircuitBoard, image: STOCK.transformer,  labels: ["High-frequency", "Custom-wound", "Isolation"] },
];

export const INDUSTRIES = [
  { slug: "ev-mobility",          title: "EV & Mobility",         description: "Chargers, on-board electronics and battery management for the electric mobility revolution.", icon: CarFront, image: STOCK.evCharging },
  { slug: "industrial-automation",title: "Industrial Automation", description: "Control panels, motor drives and process controllers for factories of the future.",            icon: Factory,  image: STOCK.robotics },
  { slug: "consumer-electronics", title: "Consumer Electronics",  description: "Smart-home devices, IoT plugs and connected appliances engineered for scale.",                  icon: Plug,     image: STOCK.smartPlug },
  { slug: "energy-and-power",     title: "Energy & Power",        description: "SMPS, transformers and power-conditioning electronics for grid and off-grid systems.",          icon: Zap,      image: STOCK.windTurbines },
  { slug: "safety-and-detection", title: "Safety & Detection",    description: "Fire detection, gas sensing and emergency alarm systems for critical infrastructure.",          icon: Flame,    image: STOCK.fireSafety },
  { slug: "vending-and-retail",   title: "Vending & Retail",      description: "Beverage, coffee and self-service vending controllers with cloud telemetry.",                   icon: Coffee,   image: STOCK.vending },
];

export const PROCESS = [
  { step: "01", title: "Design",      description: "Requirements → architecture → schematic & layout.",        icon: Lightbulb },
  { step: "02", title: "Prototype",   description: "Rapid PCB build, firmware bring-up and bench validation.", icon: CircuitBoard },
  { step: "03", title: "Manufacture", description: "SMT, through-hole, mechanical assembly and box build.",    icon: Factory },
  { step: "04", title: "Test",        description: "Functional, EMI/EMC, burn-in and reliability testing.",    icon: ShieldCheck },
  { step: "05", title: "Deliver",     description: "Packaging, logistics and after-market support.",           icon: Boxes },
];

export const USPS = [
  { title: "End-to-End Capability", description: "From concept and design through certified production — all under one roof.",         icon: Target },
  { title: "Built for Reliability", description: "Industrial-grade engineering, rigorous QA and lifecycle reliability testing.",       icon: ShieldCheck },
  { title: "Made in India",         description: "Mohali-based facility with deep local supply-chain expertise and global standards.", icon: Globe },
  { title: "Innovation-First",      description: "Continuous R&D in power electronics, EV systems and embedded IoT.",                  icon: Lightbulb },
];

export const PARTNERS = [
  "https://d357yo0u4a8v36.cloudfront.net/wp-content/uploads/2024/01/partners_1.png",
  "https://d357yo0u4a8v36.cloudfront.net/wp-content/uploads/2024/01/partners_2.png",
  "https://d357yo0u4a8v36.cloudfront.net/wp-content/uploads/2024/01/partners_3.png",
  "https://d357yo0u4a8v36.cloudfront.net/wp-content/uploads/2024/01/partners_4.png",
  "https://d357yo0u4a8v36.cloudfront.net/wp-content/uploads/2024/01/partners_5.png",
  "https://d357yo0u4a8v36.cloudfront.net/wp-content/uploads/2024/01/partners_6.png",
];

export const TESTIMONIALS = [
  { quote: "Nikhan's engineering depth and on-time delivery transformed how we ship hardware. They are a true partner — not a vendor.", author: "Procurement Head",  company: "EV mobility startup, Bengaluru" },
  { quote: "From schematic to certified production in record time. Their team caught design issues we would have missed.",               author: "CTO",                company: "Industrial automation OEM" },
  { quote: "Reliable, transparent and obsessed with quality. We've scaled three product lines with them.",                                author: "Founder",            company: "Smart-home appliances brand" },
];

export const IMAGES = {
  heroBg:          STOCK.pcbMacro1,
  aboutShot:       STOCK.engineer,
  facility:        STOCK.factory,
  pcbMacro:        STOCK.pcbMacro2,
  engineerCloseup: STOCK.engineerWork,
};
